import classNames from 'classnames';
import { graphql, Link } from 'gatsby';
import { GatsbyImage } from 'gatsby-plugin-image';
import { useIsSample } from 'gatsby-theme-hem-store/src/hooks/useIsSample';
import { useTypesenseDocumentById } from 'gatsby-theme-hem-store/src/hooks/useTypesenseDocumentById';
import get from 'lodash/get';
import isString from 'lodash/isString';
import React from 'react';

import { ProductList, sendGtmProductClickEvent } from '@/utils/sendGtmEvents';

import { GatsbyContentfulImage } from '../GatsbyContentfulImage';
import { Modal } from '../Modal';
import { Money } from '../Money';

const RichTextInstagram: React.FC<Queries.RichTextInstagramFragment> = (
  entry,
) => {
  const needsCredit =
    isString(entry.photo.description) &&
    get(entry, 'photo.metadata.tags', []).some(
      (tag) => tag.contentful_id === 'needsCredit',
    );

  const photoAlt = needsCredit
    ? `Shared by @${entry.username}. ${entry.photo.description}`
    : `Shared by @${entry.username}`;

  return (
    <Modal
      trigger={
        <button className="relative col-span-3 row-span-1 h-full w-full overflow-hidden rounded-sm">
          <GatsbyImage
            className="transition-transform"
            image={entry.photo.gatsbyImageData}
            alt={photoAlt}
          />

          <p className="absolute bottom-2 left-2 right-2 line-clamp-3 text-left text-sm font-bold text-white md:bottom-4 md:left-4 md:right-4">
            <span>Shared by </span>@{entry.username}
            {needsCredit && (
              <span className="block">{entry.photo.description}</span>
            )}
          </p>
        </button>
      }
      content={
        <div className="grid max-h-screen overflow-auto rounded-sm bg-white md:grid-cols-2 md:overflow-hidden">
          <div className="relative">
            <GatsbyImage image={entry.photo.gatsbyImageData} alt={photoAlt} />
            <p className="absolute bottom-2 left-2 right-2 text-left text-sm font-bold text-white md:bottom-4 md:left-4 md:right-4">
              <span>Shared by </span>@{entry.username}
              {needsCredit && (
                <span className="block">{entry.photo.description}</span>
              )}
            </p>
          </div>

          <div>
            <p className="hidden border-b pl-4 text-base font-bold leading-[3rem] md:block">
              Products in this photo
            </p>

            <ul className="mb-2 ml-2 mr-2 divide-y overflow-auto md:mb-4 md:ml-4 md:mr-4">
              {(entry.variants || []).map((variant, index) => (
                <VariantInPhoto
                  key={variant.sku}
                  sku={variant.sku}
                  index={index}
                />
              ))}
            </ul>
          </div>
        </div>
      }
      width="ml"
    />
  );
};

const VariantInPhoto: React.FC<{ sku: string; index: number }> = ({
  sku,
  index,
}) => {
  const { status, data } = useTypesenseDocumentById({ sku });
  const { isSample } = useIsSample(data?.slug);

  if (status === 'success') {
    const {
      slug,
      productName,
      variantName,
      imageUrl,
      hoverImageUrl,
      price,
      compareAtPrice,
    } = data;

    const isOnSale = compareAtPrice > price;

    return (
      <li>
        <Link
          to={slug}
          className="group grid grid-cols-4 gap-2 py-2 text-base text-black hover:no-underline md:gap-4 md:py-4"
          onClick={() => {
            sendGtmProductClickEvent({
              sku: sku,
              productName: productName,
              variantName: variantName,
              price: price + '',
              originalPrice: (compareAtPrice || price) + '',
              slug: slug,
              list: ProductList.UgcModal,
              position: index,
            });
          }}
        >
          <div>
            {imageUrl && (
              <div className="group-hover:hidden">
                <GatsbyContentfulImage
                  imageUrl={imageUrl}
                  alt={`${productName} ${variantName}`}
                />
              </div>
            )}
            {hoverImageUrl && (
              <div className="hidden group-hover:block">
                <GatsbyContentfulImage
                  imageUrl={hoverImageUrl}
                  alt={`${productName} ${variantName}`}
                />
              </div>
            )}
          </div>

          <div className="col-span-2 grid content-center">
            <span className="block font-bold group-hover:underline">
              {productName}
            </span>
            <span className="group-hover:underline">{variantName}</span>
          </div>

          <div
            className={classNames(
              'grid content-center text-right font-bold no-underline',
              {
                'text-red-gloss': isOnSale && !isSample,
              },
            )}
          >
            <span className="block">
              <Money amount={price} sku={sku} />
            </span>
            {isOnSale && !isSample ? (
              <span className="font-normal text-black-600 line-through">
                <Money amount={compareAtPrice} sku={sku} />
              </span>
            ) : (
              <span className="h-4" />
            )}
          </div>
        </Link>
      </li>
    );
  }

  return null;
};

export default RichTextInstagram;

export const query = graphql`
  fragment RichTextInstagram on ContentfulInstagram {
    username
    photo {
      id
      gatsbyImageData(
        formats: [AUTO, AVIF, WEBP]
        quality: 80
        aspectRatio: 0.8
        layout: FULL_WIDTH
      )
      description
      metadata {
        tags {
          contentful_id
        }
      }
    }

    variants: variantsInPhoto {
      sku
    }
  }
`;
